import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import HashTag from "../components/HashTag";
import {
  Story,
  StoryHero,
  StoryNav,
  StoryOutro,
  Section,
  Figure,
  Card,
  Prose,
  Split,
  Bullets,
  fade,
} from "../components/story";

const IMG = "/images/3gpp";

const TAGS = ["3GPP", "NTN", "NBIoT", "Standards", "RAN2", "RAN4", "Rel19", "SatelliteComms"];

const NAV = [
  ["work", "The work"],
  ["threads", "What I carried"],
  ["orlando", "Orlando"],
  ["honest", "What this isn't"],
];

/** The four groups, one line each. */
const GROUPS = [
  ["RAN2", "Radio protocols", "RRC, MAC and idle mode, where the IoT-NTN work items are led."],
  ["RAN4", "RF requirements", "Power classes, emissions, demodulation. Nothing ships until it is testable."],
  ["CT1", "NAS signalling", "Attach, tracking area updates, and the non-IP data delivery paths."],
  ["SA", "Architecture", "How a satellite component fits into EPS and 5GS at all."],
];

/** Themes I wrote or co-wrote Skylo positions on, each tied to something concrete. */
const THEMES = [
  {
    title: "Voice over Non-IP",
    spec: "CT1 · SA",
    body:
      "A real voice call over a 180 kHz narrowband satellite channel means abandoning SIP-over-IP, so the position argued for a Non-IP Data Delivery bearer as the transport. I was the air-interface SME behind that architecture internally.",
    to: "/projects/satellite-comms/nb-ntn-geo-voice",
    toLabel: "The voice call it became",
  },
  {
    title: "Diversity slotted ALOHA",
    spec: "RAN2 · CB-Msg3-EDT · Rel-19",
    body:
      "Random access over a satellite costs a preamble, a response and a round trip measured in hundreds of milliseconds, all to send a few dozen bytes. Rel-19 lets an idle device skip both and replicate Msg3 across k random occasions. It is now in TS 36.300 and TS 36.321.",
  },
  {
    title: "Multiplexed Msg4",
    spec: "RAN2 · Rel-19",
    body:
      "The other half of the same problem: if a thousand devices transmit contention-based Msg3 into one beam, answering each individually puts the cost straight back on the downlink. Skylo filed on this in 2024, before the corresponding spec text existed.",
    to: "/publications",
    toLabel: "US 2024/0357621 A1",
  },
  {
    title: "Emergency prioritisation",
    spec: "RAN2 · PRACH partitioning",
    body:
      "When one beam covers a few million square kilometres, an emergency message and a soil-moisture reading contend for the same random-access channel. Reserved PRACH partitions let the base station tell them apart before decoding anything.",
    to: "/publications",
    toLabel: "US 12,356,465 B2",
  },
  {
    title: "HPUE power classes",
    spec: "RAN4",
    body:
      "Link budget to a geostationary satellite is won and lost in a couple of dB, and high-power UE classes are one of the few places to find them. Arguing for a requirement is mostly arguing about how it will be measured.",
  },
  {
    title: "User-plane data over NTN",
    spec: "SA2 · Rel-19",
    body:
      "NB-IoT over satellite reached the field before the architecture describing it was finished. Unglamorous, load-bearing work: making the EPS architecture match what an operator running it commercially had already learned.",
  },
];

export default function ThreeGpp() {
  return (
    <Story>
      <Helmet>
        <title>3GPP Contributions to IoT-NTN | Soham Desai</title>
        <meta
          name="description"
          content="3GPP contributions as a delegate for Skylo Technologies across RAN2, RAN4, CT1 and SA on the NB-IoT non-terrestrial network standards."
        />
        <meta property="og:title" content="3GPP Contributions to IoT-NTN" />
        <meta
          property="og:description"
          content="Voice over Non-IP, diversity slotted ALOHA, HPUE power classes, and emergency prioritisation in the IoT-NTN working groups."
        />
        <meta property="og:image" content={`${IMG}/ran-orlando-2024-solo.jpg`} />
      </Helmet>

      <StoryHero
        breadcrumb={{ to: "/projects/satellite-comms", label: "5G & NTN" }}
        kicker="3GPP contributions · RAN2 / RAN4 / CT1 / SA"
        title={
          <>
            Writing the standard, not just{" "}
            <span className="text-accent">shipping against it</span>
          </>
        }
        lead={
          <>
            For a stretch I was a 3GPP delegate for{" "}
            <a
              href="https://www.skylo.tech/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-hover underline underline-offset-4 decoration-border transition-colors"
            >
              Skylo
            </a>
            , writing and defending contributions in the working groups that were assembling the
            satellite IoT standard, for a company already operating the feature they were still
            specifying.
          </>
        }
        stats={[
          { value: "4", label: "working groups: RAN2, RAN4, CT1, SA" },
          { value: "17 → 19", label: "releases the contributions spanned" },
          { value: "18M+", label: "devices the standard had to serve", amber: true },
        ]}
        chips={["NB-IoT NTN", "Rel-17 → Rel-19", "Orlando, Nov 2024"]}
      />

      <StoryNav items={NAV} />

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* ══════════ 1 · THE WORK ══════════ */}
        <Section
          id="work"
          kicker="What this actually is"
          title="A standard is an argument that ended"
          lead="3GPP writes the specifications every cellular device implements. You upload a proposal, present it for four minutes, and people whose commercial interests differ from yours explain why it is wrong. The ones that survive become normative text."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {GROUPS.map(([wg, scope, body]) => (
              <div
                key={wg}
                className="rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-lg font-semibold text-accent">{wg}</span>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {scope}
                  </span>
                </div>
                <p className="mt-2.5 text-sm text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>

          <Card accent title="The unusual part">
            <p>
              Most contributions to a satellite work item are arguments about simulation. Ours were
              frequently arguments about what had happened, because Skylo was already running a
              commercial Rel-17 NB-IoT network across seven geostationary satellites. That cuts
              both ways: operating the feature teaches you which parts of the spec are wrong in
              practice, but it also makes you the company with the most to lose from a change.
            </p>
          </Card>
        </Section>

        {/* ══════════ 2 · THREADS ══════════ */}
        <Section
          id="threads"
          kicker="The work"
          title="Six threads I carried into the room"
          lead="Each tied to something concrete: a procedure in a spec, a requirement in RAN4, or an architecture that had to exist before the service could."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {THEMES.map((t) => (
              <div
                key={t.title}
                className="flex flex-col rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <h3 className="font-display text-base font-semibold text-foreground">{t.title}</h3>
                <span className="mt-1 font-mono text-[0.7rem] uppercase tracking-wide text-accent/80">
                  {t.spec}
                </span>
                <p className="mt-3 flex-1 text-sm text-muted leading-relaxed">{t.body}</p>
                {t.to && (
                  <Link
                    to={t.to}
                    className="mt-3 inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
                  >
                    {t.toLabel}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </motion.div>
        </Section>

        {/* ══════════ 3 · ORLANDO ══════════ */}
        <Section
          id="orlando"
          kicker="The meeting week"
          title="Orlando, November 2024"
          lead="RAN1 through RAN4 met at Lake Buena Vista from 18 to 22 November 2024. Several thousand Tdocs, five days, and a hotel ballroom with the air conditioning set to a temperature nobody chose."
        >
          <Split>
            <Figure
              src={`${IMG}/ran-orlando-2024-solo.jpg`}
              alt="Soham Desai at the 3GPP Working Group Meetings backdrop, Orlando, November 2024"
              caption="3GPP RAN working-group meetings, Orlando, 18–22 November 2024."
            />
            <Figure
              src={`${IMG}/ran-orlando-2024-delegation.jpg`}
              alt="The Skylo delegation at the 3GPP Working Group Meetings, Orlando, November 2024"
              caption="The Skylo delegation at the same meeting. A position only survives the week if more than one person can defend it in more than one room."
            />
          </Split>

          <Prose>
            <p>
              The document list goes up before the meeting and it is enormous. The real preparation
              is deciding which fifteen of several thousand contributions actually threaten or help
              your position, and reading those properly. The text agreed on Friday is usually a
              compromise drafted at eleven o'clock on Thursday night.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 4 · HONESTY ══════════ */}
        <Section
          id="honest"
          kicker="Honesty · what this does not claim"
          title="What a delegate can and cannot take credit for"
        >
          <Card>
            <Bullets
              items={[
                "Nothing in a 3GPP specification has a single author. A feature that reaches normative text has been through dozens of companies.",
                "Contributions were submitted on behalf of Skylo, not by me personally. Where I say I worked on something, I wrote or co-wrote the company position and defended it in session.",
                "Plenty of contributions do not survive. These are the threads I carried, not a list of things adopted because I proposed them.",
                "Technical details here are drawn from public 3GPP material. Anything Skylo-internal stays internal.",
              ]}
            />
          </Card>

          <motion.div variants={fade}>
            <Link
              to="/publications"
              className="group inline-flex items-center gap-2 rounded-lg border border-border bg-surface/60 px-5 py-3 text-sm text-foreground hover:border-accent/40 hover:text-accent transition-colors"
            >
              The patents that came out of the same work
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </Section>

        <StoryOutro
          title="The long game underneath the product"
          tags={TAGS}
          TagComponent={HashTag}
          backTo="/projects/satellite-comms"
          backLabel="More from 5G & NTN"
        >
          <p className="mt-4 text-lg">
            A contribution written one quarter shows up in a specification a year later and in a
            shipping chipset a year after that. But it is the only mechanism that makes a satellite
            feature work on a device somebody else built, on a network somebody else runs.
          </p>
        </StoryOutro>
      </div>
    </Story>
  );
}
